
import { useEffect, useMemo, useState } from "react";
import { MessageSquareText, CalendarDays, Search, X } from "lucide-react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

const formatDate = (value) => {
  if (!value) return "—";

  const date = value?.toDate
    ? value.toDate()
    : new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = value?.toDate
    ? value.toDate()
    : new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const questionLabels = [
  "How would you rate your overall experience?",
  "How did you get to know about this clinic?",
  "How comfortable were you with the registration process and how long did it take?",
  "How satisfied are you with the information provided by the Staff and PREs?",
  "How long did you wait to meet the physiotherapist?",
  "How satisfied are you with the treatment and the way the physiotherapist attended to your problem?",
  "How satisfied are you with the cleanliness of the waiting area and washroom?",
  "How satisfied are you with the patient's transportation by the housekeeping staff?",
  "What is the most important area in the hospital where services can be improved?",
  "How likely are you to recommend this clinic?",
];

const getSearchableValue = (value) =>
  String(value ?? "").toLowerCase();

export default function PatientFeedbackDashboard() {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "patient_feedback"),
      (snapshot) => {
        const records = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        records.sort((a, b) => {
          const dateA = a.created_at?.toDate?.()?.getTime?.() || 0;
          const dateB = b.created_at?.toDate?.()?.getTime?.() || 0;
          return dateB - dateA;
        });

        setFeedbackList(records);
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("Feedback records error:", err);
        setError("Unable to load feedback. Please check Firebase permissions.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredFeedback = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return feedbackList;

    return feedbackList.filter((item) => {
      const answers = Object.values(item.responses || {}).join(" ");

      return [
        item.patient_name,
        item.patient_id,
        item.mobile,
        answers,
      ].some((value) => getSearchableValue(value).includes(term));
    });
  }, [feedbackList, search]);

  const todayCount = useMemo(() => {
    const today = new Date();

    return feedbackList.filter((item) => {
      const date = item.created_at?.toDate?.();

      return (
        date &&
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    }).length;
  }, [feedbackList]);

  const stats = [
    {
      label: "Total Feedback",
      value: feedbackList.length,
      icon: MessageSquareText,
    },
    {
      label: "Today's Feedback",
      value: todayCount,
      icon: CalendarDays,
    },
  ];

  return (
    <div className="space-y-6 text-slate-800">
      {/* Page heading */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#204080]">
            Patient Management
          </p>
          <h2 className="mt-1 text-2xl font-bold text-[#204080] sm:text-3xl">
            Patient Feedback
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            View patient responses.
          </p>
        </div>

      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-md sm:p-6"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  {stat.label}
                </p>
                <p className="mt-2 text-2xl font-bold text-[#204080]">
                  {stat.value}
                </p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-[#204080]">
                <stat.icon size={21} strokeWidth={1.8} aria-hidden="true" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feedback table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5 md:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#204080]">
              Feedback Records
            </h3>
            <p className="mt-1 text-xs text-gray-500 sm:text-sm">
              {filteredFeedback.length} record(s) found
            </p>
          </div>

          <div className="w-full sm:max-w-sm">
            <label htmlFor="feedback-search" className="sr-only">
              Search feedback
            </label>
            <div className="relative">
              <Search
                size={17}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <input
                id="feedback-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, VPJC ID or mobile..."
                className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-[#204080] focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-[#204080]" />
            Loading feedback...
          </div>
        ) : filteredFeedback.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 px-4 py-12 text-center">
            <p className="text-base font-semibold text-gray-700">
              {search ? "No matching feedback found" : "No feedback submitted yet"}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {search
                ? "Try another name, VPJC ID, or mobile number."
                : "Submitted patient feedback will appear here."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead className="bg-slate-50">
                <tr>
                  {[
                    "S.No",
                    "Patient Name",
                    "VPJC ID",
                    "Mobile",
                    // "Suggestions",
                    "Submitted On",
                    "Action",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="whitespace-nowrap border-b border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase text-[#204080] sm:text-sm"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {filteredFeedback.map((item, index) => (
                  <tr
                    key={item.id}
                    className="transition-colors hover:bg-slate-50 even:bg-slate-50/50"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                      {item.patient_name || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {item.patient_id ? (
                        <span className="rounded-md bg-blue-50 px-2.5 py-1 font-semibold text-[#204080]">
                          {item.patient_id}
                        </span>
                      ) : (
                        <span className="text-gray-400">Not provided</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {item.mobile || "—"}
                    </td>
                    {/* <td className="max-w-[220px] px-4 py-3 text-sm text-gray-600">
                      {item.suggestions?.trim() ? (
                        <span className="line-clamp-2">
                          {item.suggestions}
                        </span>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td> */}
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setSelectedFeedback(item)}
                        className="rounded-md border border-[#204080] px-3 py-1.5 text-xs font-semibold text-[#204080] transition hover:bg-[#204080] hover:text-white"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback details modal */}
      {selectedFeedback && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-slate-950/50 px-3 py-5 backdrop-blur-[3px] sm:px-6 sm:py-8"
          onClick={() => setSelectedFeedback(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-details-title"
            className="my-0 max-h-[calc(100vh-2.5rem)] w-full max-w-4xl overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl sm:max-h-[calc(100vh-4rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#204080]">
                  Feedback Record
                </p>
                <h3
                  id="feedback-details-title"
                  className="mt-1 text-lg font-bold text-[#204080] sm:text-xl"
                >
                  {selectedFeedback.patient_name || "Patient Feedback"}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Submitted: {formatDateTime(selectedFeedback.created_at)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedFeedback(null)}
                aria-label="Close details"
                className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-5 p-4 sm:p-6">
              {/* PATIENT DETAILS */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  ["Patient Name", selectedFeedback.patient_name],
                  ["Patient VPJC ID", selectedFeedback.patient_id],
                  ["Mobile Number", selectedFeedback.mobile],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {label}
                    </p>
                    <p className="mt-1 break-words text-sm font-medium text-gray-800">
                      {value || "—"}
                    </p>
                  </div>
                ))}
              </div>

              {/* SUGGESTIONS */}
              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <h4 className="mb-2 text-base font-bold text-[#204080]">
                  Suggestions / Additional Comments
                </h4>

                <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
                  {selectedFeedback.suggestions?.trim() || "No suggestions provided."}
                </p>
              </section>

              {/* PATIENT SIGNATURE */}
              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <h4 className="mb-3 text-base font-bold text-[#204080]">
                  Patient Signature
                </h4>

                {selectedFeedback.patient_signature ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-slate-50 p-3">
                    <img
                      src={selectedFeedback.patient_signature}
                      alt="Patient Signature"
                      className="max-h-32 max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    No signature available.
                  </p>
                )}
              </section>

              {/* PATIENT RESPONSES */}
              <section>
                <h4 className="mb-3 text-base font-bold text-[#204080]">
                  Patient Responses
                </h4>

                <div className="space-y-3">
                  {questionLabels.map((label, index) => {
                    const key = `q${index + 1}`;
                    const response =
                      selectedFeedback.responses?.[key];
                      const otherDetail =
                      key === "q2"
                        ? selectedFeedback.other_details?.referral_source
                        : key === "q9"
                          ? selectedFeedback.other_details?.improvement_area
                          : "";

                    return (
                      <div
                        key={key}
                        className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4"
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {key.toUpperCase()}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-gray-800">
                          {label}
                        </p>
                        <p className="mt-2 break-words text-sm text-gray-700">
                          {response !== undefined &&
                          response !== null &&
                          response !== ""
                            ? Array.isArray(response)
                              ? response.join(", ")
                              : String(response)
                            : "No response"}
                        </p>
                        {otherDetail?.trim() && (
                        <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
                          <p className="text-xs font-semibold text-[#204080]">
                            Other Details
                          </p>
                          <p className="mt-1 break-words text-sm text-slate-700">
                            {otherDetail}
                          </p>
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
              <button
                type="button"
                onClick={() => setSelectedFeedback(null)}
                className="rounded-lg bg-[#204080] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#183365]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
